
import React, { useState } from 'react';
import { FaFile, FaTimes, FaTrash } from 'react-icons/fa';
import Stepper from './Stepper';
import ProcessImg from './ProcessImg';
import * as toGeoJSON from 'togeojson';
import { useGeoJson } from '../../contexts/GeoJsonContext';
import * as turf from '@turf/turf';
import Swal from 'sweetalert2';

const UploadImg = ({ setOpenImgSelecter }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);
  const [processBtn, setProcessBtn] = useState(false);
  const { setGeoJsonData, setGeoArea } = useGeoJson(); // Use the context to update geoJsonData
  const [isAreaValid, setIsAreaValid] = useState(false);


  // Handle file change (for file input)
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.name.endsWith(".kml") || file.name.endsWith(".kmz"))) {
      setSelectedFile(file);
      handleFileRead(file); // Read and process the file
    } else {
      setError("Invalid file type. Please upload a .kml or .kmz file.");
    }
  };

  // Handle file drop (for drag and drop)
  const handleFileDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith(".kml") || file.name.endsWith(".kmz"))) {
      setSelectedFile(file);
      handleFileRead(file); // Read and process the file
    } else {
      setError("Invalid file type. Please upload a .kml or .kmz file.");
    }
  };

  // Allow drag over the file drop area
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Remove the selected file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setGeoJsonData(null); // Clear parsed data when file is removed
    setError(null); // Clear any existing errors
  };

  // Parse KML and convert it to GeoJSON
  const handleFileRead = (file) => {
    const reader = new FileReader();
    reader.onload = function (event) {
      try {
        const kmlText = event.target.result;

        const parser = new DOMParser();
        const kml = parser.parseFromString(kmlText, "application/xml");

        // Check for parsing errors
        const parserError = kml.getElementsByTagName("parsererror");
        if (parserError.length > 0) {
          console.error("Parser Error:", parserError[0].textContent);
          setError("Failed to parse KML file. Invalid file structure.");
          return;
        }

        // Convert the KML to GeoJSON using togeojson
        const geoJson = toGeoJSON.kml(kml);
        const coordinates = geoJson.features[0]?.geometry?.coordinates;

        if (coordinates) {
          // Flatten the nested array of coordinates if needed
          const allCoordinates = coordinates.flat();

          // Swap longitude and latitude
          const swappedCoordinates = allCoordinates.map((coord) => [coord[1], coord[0], coord[2]]);

          if (swappedCoordinates.length > 0) {
            const polygon = turf.polygon([allCoordinates]);
            const areaInSquareMeters = turf.area(polygon);
            const areaInAcres = areaInSquareMeters / 4046.86;

            // Reject if area exceeds 100 acres
            if (areaInAcres > 100) {
              setError("KML area exceeds 100 acres. Please upload a smaller area.");
              setProcessBtn(false);
              setIsAreaValid(false);
              return;
            }

            setGeoArea(areaInAcres.toFixed(2));
            setIsAreaValid(true);
          }

          // Update the geoJson data if area is valid
          setGeoJsonData(swappedCoordinates);
        } else {
          setError("Could not find coordinates in the KML file.");
        }

        // Clear any previous error if everything is fine
        setError(null);

      } catch (err) {
        console.error("File Read Error:", err);
        setError("Error reading the file. Please try again.");
      }
    };

    reader.readAsText(file); // Start reading the file as text
  };


  return (
    <div className="absolute w-full h-screen bg-black bg-opacity-50 top-0 left-0 right-0 z-[10000] flex items-center justify-center">
      <div
        className={`relative w-[90%] md:w-[80%] lg:w-[50%] ${selectedFile ? "h-[680px]" : "h-[400px]"
          } bg-white flex flex-col items-center justify-start p-2 rounded-xl`}
      >
        <span
          className="absolute right-2 top-3 text-xl cursor-pointer hover:scale-105 duration-300 bg-gray-300 rounded-full p-2"
          onClick={() => setOpenImgSelecter(false)}
        >
          <FaTimes />
        </span>
        <Stepper />
        <div className="mt-5 w-[70%] mx-auto text-center">
          <p className="text-2xl font-semibold">Add files with field boundaries</p>
          <p className="mt-1 text-sm font-medium text-gray-500">
            We support .kml files for field boundaries.
          </p>
        </div>

        <div className="bg-gray-200 p-3 w-[70%] mt-4 rounded-xl">
          <div
            className="min-h-[100px] border-dashed border-2 border-gray-400 rounded-md p-4 flex items-center justify-center flex-col"
            onDrop={handleFileDrop}
            onDragOver={handleDragOver}
          >
            <div className="flex flex-col items-center justify-center">
              <p className="font-semibold text-gray-600">Drag and drop files here</p>
              <p className="text-sm font-medium text-gray-500">1.0 GB max</p>
              <FaFile className="text-xl text-gray-400 mt-2" />
              <input
                type="file"
                onChange={handleFileChange}
                style={{ display: "none" }}
                id="fileInput"
                accept=".kmz,.kml"
              />
              <label
                htmlFor="fileInput"
                className="bg-green-500 rounded-md px-4 py-2 font-medium cursor-pointer hover:bg-green-600 duration-300 text-white mt-2"
              >
                Select files
              </label>
            </div>
          </div>
          {selectedFile && (
            <div className="flex mt-4 w-full justify-between items-center">
              <div className="flex justify-between items-center">
                <FaFile className="text-2xl text-gray-400 mr-2" />
                <div>
                  <p className="text-sm">{selectedFile.name}</p>
                  <p className="text-[12px] text-gray-400">{selectedFile.size} B</p>
                </div>
              </div>
              <span
                onClick={handleRemoveFile}
                className="p-2 bg-gray-300 rounded-md cursor-pointer text-black hover:text-red-500 duration-300"
              >
                <FaTrash />
              </span>
            </div>
          )}
        </div>

        {selectedFile ? (
          <>
            <div className="flex items-center justify-between w-[50%] mt-4 bg-[#333] p-1 rounded-md gap-2">
              <button
                onClick={() => setSelectedFile(null)}
                className="w-1/2 py-3 rounded bg-[#474747] text-white border-none outline-none cursor-pointer hover:bg-black duration-300 hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={() => isAreaValid && setProcessBtn(true)}
                disabled={!isAreaValid}
                className={`w-1/2 py-3 rounded text-white border-none outline-none cursor-pointer duration-300 
    ${isAreaValid ? "bg-green-500 hover:bg-green-600" : "bg-gray-400 cursor-not-allowed"}`}
              >
                Process files
              </button>

            </div>
            {processBtn && (
              <ProcessImg
                fileName={selectedFile?.name}
                setProcessBtn={setProcessBtn}
                setSelector={setOpenImgSelecter}
              />
            )}
          </>
        ) : (
          <p className="text-gray-500 mt-4">No file selected.</p>
        )}

        {error && <p className="text-red-500 mt-2">{error}</p>} {/* Display error */}
      </div>
    </div>

  );
};

export default UploadImg;


